import {
  Citations,
  OnboardingHeader,
  OnboardingFooter,
  ExplainSlideButton,
  OnboardingNavigationMenu,
  OnboardingResultsStatusScreen,
  useSlideConfig,
  SLIDE_VIDEO_URLS,
} from '@/features/client-onboarding';
import { OnboardingResultsPending } from './onboarding-results-pending';
import { resolveOnboardingGate } from '../lib/resolve-onboarding-gate';
import { FullScreenLoadingOverlay } from '@/components/FullScreenLoadingOverlay';
import { SampleClientBanner } from '@/shared/components/sample-client-banner';

import { useSelector } from 'react-redux';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

import { selectClient, selectClientIsLoading, selectFeatureFlags, selectSession } from '@/model';

import type { ReactNode } from 'react';
import { cn } from '@design-system';

export function OnboardingLayout({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const client = useSelector(selectClient)!;
  const session = useSelector(selectSession);
  const clientIsLoading = useSelector(selectClientIsLoading);
  const featureFlags = useSelector(selectFeatureFlags);

  const { flags } = useFeatureFlags();
  const { slidePage, citations } = useSlideConfig();

  const useFormProvider = flags.useFormProvider ?? false;

  const onboardingGate = resolveOnboardingGate({
    client,
    sessionType: session.sessionType,
    useFormProvider,
    isD2CMode: !!featureFlags.d2cMode,
    isGenworthMode: !!featureFlags.genworthMode,
    isEdjPilotAdvisor: !!featureFlags.edjPilotAdvisor,
  });

  if (onboardingGate === 'complete-intake') {
    return (
      <OnboardingResultsStatusScreen
        variant="pending"
        onRefresh={() => window.location.reload()}
      />
    );
  }

  if (onboardingGate === 'results-locked') {
    return <OnboardingResultsPending />;
  }

  const videoUrl = slidePage ? SLIDE_VIDEO_URLS.get(slidePage) : undefined;

  return (
    <div className="bg-background relative flex h-dvh flex-col">
      <FullScreenLoadingOverlay isLoading={clientIsLoading} />

      {client.isSampleClient && <SampleClientBanner />}

      <div className="z-30 shrink-0">
        <OnboardingHeader />
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className={cn('relative mx-auto h-full w-full max-w-5xl', className)}>
          {children}
          {videoUrl && (
            <ExplainSlideButton
              videoUrl={videoUrl}
              className="hidden xl:block"
            />
          )}
        </div>
      </div>

      <div className="z-30 shrink-0">
        <OnboardingFooter />
      </div>

      <OnboardingNavigationMenu />
    </div>
  );
}
